import { formatDateTimeShort } from "@/shared/lib/date-time";
import type { ShiftReport } from "../../domain/repo/get-shift-report";

/* ขนาดเท่ากับ bill อื่น (receipt / proforma / kitchen): 72.1mm */
const STYLES = `
  html, body { width: 72.1mm; max-width: 72.1mm; min-width: 72.1mm; margin: 0 auto; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; color: #000; background: #fff; font-size: 15px; line-height: 1.5; font-weight: 500; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  *, *::before, *::after { box-sizing: border-box; }
  .print-page { page-break-after: always; }
  .print-page:last-child { page-break-after: auto; }
  .report-header { text-align: center; margin-bottom: 8px; }
  .report-title { font-size: 22px; font-weight: bold; letter-spacing: 0.5px; }
  .report-meta { font-size: 13px; color: #000; margin-top: 2px; font-weight: 500; }
  .section { margin-bottom: 10px; }
  .section-title { font-size: 15px; font-weight: bold; margin-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 15px; font-weight: 500; }
  td { padding: 3px 0; vertical-align: top; }
  td:last-child { text-align: right; white-space: nowrap; font-size: 15px; }
  .total-row { font-weight: bold; border-top: 1px solid #000; padding-top: 4px; margin-top: 2px; }
  .variance-negative { color: #b91c1c; }
  .variance-positive { color: #15803d; }
  .bill-footer { text-align: center; margin-top: 12px; font-size: 13px; color: #000; font-weight: 500; }
  @media print {
    @page { size: 72.1mm auto; margin: 0; }
    html, body { width: 72.1mm !important; max-width: 72.1mm !important; min-width: 72.1mm !important; margin: 0 !important; padding: 4mm !important; font-weight: 500 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body, .report-meta, table, td, .bill-footer { font-weight: 500 !important; }
    .report-title, .section-title, .total-row { font-weight: bold !important; }
  }
`;

function schedulePrint(targetWindow: Window, onAfterPrint?: () => void): void {
  targetWindow.addEventListener(
    "afterprint",
    () => {
      onAfterPrint?.();
    },
    { once: true },
  );
  setTimeout(() => {
    targetWindow.focus();
    targetWindow.print();
  }, 200);
}

function printShiftReportInNewWindow(html: string): void {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    throw new Error("Cannot open print window — please allow pop-ups");
  }
  printWindow.document.write(html);
  printWindow.document.close();
  schedulePrint(printWindow, () => {
    try {
      printWindow.close();
    } catch {
      // ignore
    }
  });
}

function formatNum(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * สร้าง HTML สำหรับ Shift Report แล้วเปิดหน้าต่างพิมพ์
 */
export function buildShiftReportHtml(report: ShiftReport): string {
  const { shift, branchName, deviceName, openedByName, closedByName, cashSummary, paymentSummary, orderSummary } = report;

  const openedAtStr = shift.openedAt ? formatDateTimeShort(shift.openedAt) : "—";
  const closedAtStr = shift.closedAt ? formatDateTimeShort(shift.closedAt) : "—";

  const cashRows = [
    ["Opening Float", formatNum(cashSummary.openingFloat)],
    ["Cash Received", formatNum(cashSummary.cashReceived)],
    ["Cash Refunded", `-${formatNum(cashSummary.cashRefunded)}`],
    ["Pay In", formatNum(cashSummary.payIn)],
    ["Pay Out", `-${formatNum(cashSummary.payOut)}`],
    ["Expected Cash", formatNum(cashSummary.expectedCash)],
    ["Closing Amount", formatNum(cashSummary.closingAmount)],
    ["Variance", formatNum(cashSummary.variance)],
  ];
  const cashTableRows = cashRows
    .map(
      ([label, value]) =>
        `<tr${label === "Variance" ? ` class="${cashSummary.variance < 0 ? "variance-negative" : cashSummary.variance > 0 ? "variance-positive" : ""}"` : ""}><td>${label}</td><td>${value} ₭</td></tr>`,
    )
    .join("");

  const paymentRows = paymentSummary
    .map(
      (p) =>
        `<tr><td>${p.methodDisplayName} (${p.type})</td><td>${formatNum(p.grossAmount)} ₭</td></tr>`,
    )
    .join("");
  const paymentTotal = paymentSummary.reduce((s, p) => s + p.grossAmount, 0);
  const paymentTable =
    paymentRows +
    (paymentSummary.length > 0
      ? `<tr class="total-row"><td>Total Payments</td><td>${formatNum(paymentTotal)} ₭</td></tr>`
      : "");

  const orderSection = `
  <div class="section">
    <div class="section-title">Order Summary</div>
    <table>
      <tr><td>Paid Orders</td><td>${orderSummary.orderCount}</td></tr>
      <tr><td>Gross (before discount)</td><td>${formatNum(orderSummary.grossBeforeDiscount)} ₭</td></tr>
      <tr><td>Total Discount</td><td>${orderSummary.totalDiscount > 0 ? "-" : ""}${formatNum(orderSummary.totalDiscount)} ₭</td></tr>
      <tr class="total-row"><td>Net Sales</td><td>${formatNum(orderSummary.netSales)} ₭</td></tr>
    </table>
  </div>`;

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Shift Report - ${branchName ?? "Branch"} - ${openedAtStr}</title>
  <style>${STYLES}</style>
</head>
<body>
  <div class="print-page">
    <div class="report-header">
      <div class="report-title">Shift Report</div>
      <div class="report-meta">${branchName ?? ""} ${deviceName ? ` · ${deviceName}` : ""}</div>
    </div>

    <div class="section">
      <div class="section-title">Shift Info</div>
      <table>
        <tr><td>Opened By</td><td>${openedByName ?? shift.openedBy}</td></tr>
        <tr><td>Opened At</td><td>${openedAtStr}</td></tr>
        <tr><td>Closed By</td><td>${closedByName ?? shift.closedBy ?? "—"}</td></tr>
        <tr><td>Closed At</td><td>${closedAtStr}</td></tr>
        ${shift.notes ? `<tr><td>Notes</td><td>${shift.notes}</td></tr>` : ""}
      </table>
    </div>

    <div class="section">
      <div class="section-title">Cash in Drawer</div>
      <table>
        ${cashTableRows}
      </table>
    </div>

    <div class="section">
      <div class="section-title">Payments by Method</div>
      <table>
        ${paymentTable || "<tr><td colspan=\"2\">—</td></tr>"}
      </table>
    </div>

    ${orderSection}

    <div class="bill-footer">Printed at: ${formatDateTimeShort(new Date().toISOString())}</div>
  </div>
</body>
</html>`;
}

/**
 * พิมพ์ Shift Report — ใช้ hidden iframe เพื่อไม่โดน popup blocker
 * (หลังปิดกะมี async หลายขั้น window.open มักถูกบล็อก)
 */
export function printShiftReport(report: ShiftReport): void {
  const html = buildShiftReportHtml(report);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Shift report print");
  iframe.setAttribute(
    "style",
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0",
  );
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDocument = frameWindow?.document;
  if (!frameWindow || !frameDocument) {
    iframe.remove();
    printShiftReportInNewWindow(html);
    return;
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();
  schedulePrint(frameWindow, () => {
    iframe.remove();
  });
}
